/**
 * Still Here / Late Drop Off Reminder Dialog
 * Separate feature from Pickup Safety Alerts — lets a director pick up to two
 * daily times and, for each, which children to watch. Once a time passes, if
 * a selected child is still signed in, their primary pickup person gets a
 * reminder (email and/or SMS, reusing the shared Parent Communication SMS
 * entitlement — no new billing product). Also includes a separate Late Drop
 * Off Reminder: a friendly SMS/email sent once, right at sign-in, if a child
 * arrives after a director-chosen morning cutoff time.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Info, Clock, Printer, Trash2, History as HistoryIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// Child records store name as "Last, First" — this dialog displays it as
// "First Last" instead. Display-only; never written back anywhere.
const displayFirstLast = (name) => {
  if (!name || !name.includes(",")) return name || "";
  const [last, first] = name.split(",").map((s) => s.trim());
  return first ? `${first} ${last}` : name;
};

const formatTime12h = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return "";
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
};

const DEFAULT_SETTINGS = {
  enabled: false,
  time1: "",
  time2: "",
  time1ChildIds: [],
  time2ChildIds: [],
  emailEnabled: true,
  smsEnabled: false,
  additionalNote: "",
  directorAlertEnabled: false,
  directorAlertPhone: "",
  directorAlertStaffName: "",
  dropOffReminderEnabled: false,
  dropOffCutoffTime: "",
  dropOffEmailEnabled: false,
  dropOffSmsEnabled: false,
  dropOffAdditionalNote: "",
  contactOverrides: {},
};

export const StillHereRemindersDialog = ({ open, onOpenChange, children }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [clearingHistory, setClearingHistory] = useState(false);
  const [staffNames, setStaffNames] = useState([]);

  const activeChildren = (children || [])
    .filter((c) => c.active !== false)
    .sort((a, b) => a.name.localeCompare(b.name));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/still-here-reminders/settings`, authHeaders());
      setSettings({ ...DEFAULT_SETTINGS, ...res.data });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load Still Here Reminders settings");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/still-here-reminders/history`, authHeaders());
      setHistory(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load reminder history");
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const loadStaffNames = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/staff-records`, authHeaders());
      setStaffNames((res.data?.records || []).map((r) => r.name).filter(Boolean));
    } catch (err) {
      // Non-critical — the staff dropdown is label-only; failing to load it
      // should never block the rest of the Settings tab.
      setStaffNames([]);
    }
  }, []);

  useEffect(() => {
    if (open) {
      load();
      loadHistory();
      loadStaffNames();
    }
  }, [open, load, loadHistory, loadStaffNames]);

  const toggleChild = (slotKey, childId) => {
    setSettings((prev) => {
      const list = prev[slotKey];
      const next = list.includes(childId) ? list.filter((id) => id !== childId) : [...list, childId];
      return { ...prev, [slotKey]: next };
    });
  };

  const toggleSelectAll = (slotKey) => {
    setSettings((prev) => {
      const allIds = activeChildren.map((c) => c.id);
      const allSelected = allIds.length > 0 && allIds.every((id) => prev[slotKey].includes(id));
      return { ...prev, [slotKey]: allSelected ? [] : allIds };
    });
  };

  const updateContactOverride = (childId, field, value) => {
    setSettings((prev) => ({
      ...prev,
      contactOverrides: {
        ...prev.contactOverrides,
        [childId]: { ...(prev.contactOverrides[childId] || {}), [field]: value },
      },
    }));
  };

  const selectedReminderChildren = activeChildren.filter(
    (c) => settings.time1ChildIds.includes(c.id) || settings.time2ChildIds.includes(c.id)
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/still-here-reminders/settings`, settings, authHeaders());
      setSettings({ ...DEFAULT_SETTINGS, ...res.data });
      toast.success("Still Here Reminders settings saved");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const handlePrintHistory = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const rows = history.map((h) => `<tr>
      <td>${displayFirstLast(h.childName) || ""}</td>
      <td>${h.timeSlot === "dropoff" ? "Late Drop Off" : h.timeSlot === "time1" ? "Reminder Time 1" : "Reminder Time 2"}</td>
      <td>${h.notificationType === "email" ? "Email" : "Text (SMS)"}</td>
      <td>${h.recipient || "-"}</td>
      <td><span class="badge" style="background:${h.success ? "#dcfce7" : "#fee2e2"};color:${h.success ? "#15803d" : "#b91c1c"}">${h.success ? "Sent" : "Failed"}</span></td>
      <td>${h.sentAt ? new Date(h.sentAt).toLocaleString("en-US") : "-"}</td>
    </tr>`).join("");
    w.document.write(`<!DOCTYPE html><html><head><title>Still Here Reminders - History</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Segoe UI',system-ui,sans-serif;color:#1a1a1a;padding:40px 50px;max-width:900px;margin:0 auto;font-size:13px;line-height:1.4}
.header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #1e3a5f;padding-bottom:16px;margin-bottom:24px}
.header h1{font-size:22px;font-weight:700;color:#1e3a5f;letter-spacing:-0.5px}
.header .subtitle{font-size:12px;color:#6b7280;margin-top:2px}
.logo{height:50px;width:auto;margin-bottom:8px}
table{width:100%;border-collapse:collapse;font-size:12px;margin-top:6px}
th{background:#f8fafc;color:#374151;font-weight:600;text-align:left;padding:8px 10px;border-bottom:2px solid #e5e7eb}
td{padding:7px 10px;border-bottom:1px solid #f3f4f6}
.badge{display:inline-block;padding:2px 8px;border-radius:3px;font-size:11px;font-weight:600}
.footer{margin-top:30px;padding-top:12px;border-top:1px solid #e5e7eb;font-size:10px;color:#9ca3af;text-align:center}
@media print{body{padding:20px 30px}@page{margin:0.5in}}
</style></head><body>
<div class="header">
  <div><img src="/simple-sign-in-logo.png" class="logo" alt="Simple Sign In" onerror="this.style.display='none'" /><h1>Still Here Reminders — History</h1><div class="subtitle">Reminder Send Log</div></div>
  <div style="text-align:right"><div class="subtitle">${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</div></div>
</div>
<table><thead><tr><th>Child</th><th>Time Slot</th><th>Channel</th><th>Recipient</th><th>Status</th><th>Sent At</th></tr></thead>
<tbody>${rows || '<tr><td colspan="6" style="text-align:center;color:#9ca3af;padding:20px">No reminders sent yet.</td></tr>'}</tbody></table>
<div class="footer">Generated by Simple Sign In</div>
</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  const handleClearHistory = async () => {
    if (history.length === 0) return;
    if (!window.confirm(
      `Clear all ${history.length} entries from the reminder history?\n\n` +
      `This only hides them from this view — nothing is permanently deleted.`
    )) return;
    setClearingHistory(true);
    try {
      await axios.post(`${API}/still-here-reminders/history/clear`, {}, authHeaders());
      toast.success("Reminder history cleared");
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not clear history");
    } finally {
      setClearingHistory(false);
    }
  };

  const renderColumn = (label, slotKey, timeKey) => {
    const allIds = activeChildren.map((c) => c.id);
    const allSelected = allIds.length > 0 && allIds.every((id) => settings[slotKey].includes(id));
    return (
      <div className="flex-1 border border-gray-200 rounded-lg p-3" data-testid={`still-here-column-${slotKey}`}>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-gray-700">
            {label} {settings[timeKey] && <span className="text-gray-400 font-normal">({formatTime12h(settings[timeKey])})</span>}
          </p>
          <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
            <Checkbox
              checked={allSelected}
              onCheckedChange={() => toggleSelectAll(slotKey)}
              data-testid={`still-here-select-all-${slotKey}`}
            />
            Select All
          </label>
        </div>
        <ScrollArea className="h-48 pr-2">
          <div className="space-y-1.5">
            {activeChildren.length === 0 && (
              <p className="text-xs text-gray-400 italic">No active children.</p>
            )}
            {activeChildren.map((c) => {
              const otherSlot = slotKey === "time1ChildIds" ? "time2ChildIds" : "time1ChildIds";
              const bothSelected = settings[slotKey].includes(c.id) && settings[otherSlot].includes(c.id);
              return (
                <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer py-0.5"
                  data-testid={`still-here-child-${slotKey}-${c.id}`}>
                  <Checkbox
                    checked={settings[slotKey].includes(c.id)}
                    onCheckedChange={() => toggleChild(slotKey, c.id)}
                  />
                  <span className="text-gray-700">{displayFirstLast(c.name)}</span>
                  {bothSelected && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-300 text-amber-700 bg-amber-50"
                      data-testid={`still-here-both-badge-${c.id}`}>
                      Both selected
                    </Badge>
                  )}
                </label>
              );
            })}
          </div>
        </ScrollArea>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="still-here-reminders-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-600" />
            Still Here / Late Drop Off Reminder
          </DialogTitle>
          <DialogDescription>
            Notify a child's pickup contact if they're still signed in at a time you choose, or if they were
            signed in after your morning drop-off cutoff.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="settings" className="w-full">
          <TabsList data-testid="still-here-tabs">
            <TabsTrigger value="settings" data-testid="still-here-tab-settings">Settings</TabsTrigger>
            <TabsTrigger value="history" data-testid="still-here-tab-history">
              <HistoryIcon className="w-3.5 h-3.5 mr-1.5" /> History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="settings">
            {loading ? (
              <p className="text-sm text-gray-500 py-8 text-center">Loading…</p>
            ) : (
              <div className="space-y-4 pt-2">
                <div className="flex items-center justify-between border border-gray-200 rounded-lg p-3">
                  <Label htmlFor="still-here-enabled" className="text-sm font-medium cursor-pointer">
                    Enable Still Here Reminders
                  </Label>
                  <Switch
                    id="still-here-enabled"
                    checked={settings.enabled}
                    onCheckedChange={(checked) => setSettings((prev) => ({ ...prev, enabled: checked }))}
                    data-testid="still-here-enabled-switch"
                  />
                </div>

                <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-800">
                    Reminders are checked every 15 minutes, on the hour and at :15, :30, and :45. A time
                    set to one of those marks (e.g., 4:45 PM) sends right on time; other times may arrive
                    up to 15 minutes later, at the next check.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="still-here-time1">Reminder Time 1</Label>
                    <Input id="still-here-time1" type="time" data-testid="still-here-time1-input"
                      value={settings.time1 || ""}
                      onChange={(e) => setSettings((prev) => ({ ...prev, time1: e.target.value }))} />
                  </div>
                  <div>
                    <Label htmlFor="still-here-time2">Reminder Time 2</Label>
                    <Input id="still-here-time2" type="time" data-testid="still-here-time2-input"
                      value={settings.time2 || ""}
                      onChange={(e) => setSettings((prev) => ({ ...prev, time2: e.target.value }))} />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  {renderColumn("Reminder Time 1", "time1ChildIds", "time1")}
                  {renderColumn("Reminder Time 2", "time2ChildIds", "time2")}
                </div>

                <div className="border border-gray-200 rounded-lg p-3 space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={settings.emailEnabled}
                      onCheckedChange={(checked) => setSettings((prev) => ({ ...prev, emailEnabled: checked }))}
                      data-testid="still-here-email-checkbox"
                    />
                    Send by email
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={settings.smsEnabled}
                      onCheckedChange={(checked) => setSettings((prev) => ({ ...prev, smsEnabled: checked }))}
                      data-testid="still-here-sms-checkbox"
                    />
                    Send by text (SMS)
                  </label>
                  {settings.smsEnabled && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                      Each reminder sent by text counts toward your SMS usage and any applicable charges.
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="still-here-additional-note">Additional Note (optional)</Label>
                  <Input
                    id="still-here-additional-note"
                    placeholder="e.g., Late fees may apply."
                    value={settings.additionalNote || ""}
                    onChange={(e) => setSettings((prev) => ({ ...prev, additionalNote: e.target.value }))}
                    data-testid="still-here-additional-note-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Added as an extra line at the end of the reminder message. Leave blank to keep the message as-is.
                  </p>
                </div>

                {selectedReminderChildren.length > 0 && (
                  <div className="border border-gray-200 rounded-lg p-3 space-y-3" data-testid="still-here-contact-overrides">
                    <p className="text-sm font-semibold text-gray-700">Contact Info for Reminders</p>
                    <p className="text-xs text-gray-500">
                      Pre-filled from each child's primary pickup contact. Editing here only changes who
                      receives these reminders — it will not change this child's file anywhere else in the app.
                    </p>
                    <div className="space-y-2">
                      {selectedReminderChildren.map((c) => {
                        const primary = (c.pickupPersons || [])[0] || {};
                        const override = settings.contactOverrides[c.id] || {};
                        return (
                          <div
                            key={c.id}
                            className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center border-t border-gray-100 pt-2 first:border-t-0 first:pt-0"
                            data-testid={`still-here-contact-row-${c.id}`}
                          >
                            <span className="text-sm text-gray-700 font-medium">{displayFirstLast(c.name)}</span>
                            <Input
                              placeholder="Name"
                              value={override.name ?? primary.name ?? ""}
                              onChange={(e) => updateContactOverride(c.id, "name", e.target.value)}
                              data-testid={`still-here-contact-name-${c.id}`}
                            />
                            <Input
                              placeholder="Phone"
                              value={override.phone ?? primary.phone ?? ""}
                              onChange={(e) => updateContactOverride(c.id, "phone", e.target.value)}
                              data-testid={`still-here-contact-phone-${c.id}`}
                            />
                            <Input
                              placeholder="Email"
                              value={override.email ?? primary.email ?? ""}
                              onChange={(e) => updateContactOverride(c.id, "email", e.target.value)}
                              data-testid={`still-here-contact-email-${c.id}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="border border-sky-200 bg-sky-50/50 rounded-lg p-3 space-y-3" data-testid="still-here-director-alert-section">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="director-alert-enabled" className="text-sm font-medium cursor-pointer">
                      Also text a staff member when a reminder is sent
                    </Label>
                    <Switch
                      id="director-alert-enabled"
                      checked={settings.directorAlertEnabled}
                      onCheckedChange={(checked) => setSettings((prev) => ({ ...prev, directorAlertEnabled: checked }))}
                      data-testid="director-alert-enabled-switch"
                    />
                  </div>
                  {settings.directorAlertEnabled && (
                    <>
                      <p className="text-xs text-gray-500">
                        Sends a text to the number below once a parent's Still Here reminder goes out by text, so
                        a missed sign-out doesn't go unnoticed. Text only — not sent for Late Drop Off.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="director-alert-staff-name">Staff Name</Label>
                          <Select
                            value={settings.directorAlertStaffName || ""}
                            onValueChange={(value) => setSettings((prev) => ({ ...prev, directorAlertStaffName: value }))}
                          >
                            <SelectTrigger id="director-alert-staff-name" data-testid="director-alert-staff-select">
                              <SelectValue placeholder="Select staff (for your reference only)" />
                            </SelectTrigger>
                            <SelectContent>
                              {staffNames.map((n) => (
                                <SelectItem key={n} value={n} data-testid={`director-alert-staff-option-${n}`}>
                                  {n}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-gray-400 mt-1">
                            Just a label so you know whose number this is — it won't fill in the phone number below.
                          </p>
                        </div>
                        <div>
                          <Label htmlFor="director-alert-phone">Phone Number</Label>
                          <Input
                            id="director-alert-phone"
                            placeholder="(555) 123-4567"
                            value={settings.directorAlertPhone || ""}
                            onChange={(e) => setSettings((prev) => ({ ...prev, directorAlertPhone: e.target.value }))}
                            data-testid="director-alert-phone-input"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="dropoff-reminder-enabled" className="text-sm font-medium cursor-pointer">
                      Enable Late Drop Off Reminder
                    </Label>
                    <Switch
                      id="dropoff-reminder-enabled"
                      checked={settings.dropOffReminderEnabled}
                      onCheckedChange={(checked) => setSettings((prev) => ({ ...prev, dropOffReminderEnabled: checked }))}
                      data-testid="dropoff-reminder-enabled-switch"
                    />
                  </div>
                  <p className="text-xs text-amber-800">
                    Sends a friendly reminder if a child is signed in after this time — fires once, right when
                    they're signed in (not on the 15-minute check above).
                  </p>
                  <div>
                    <Label htmlFor="dropoff-cutoff-time">Drop-off cutoff time</Label>
                    <Input
                      id="dropoff-cutoff-time"
                      type="time"
                      data-testid="dropoff-cutoff-time-input"
                      value={settings.dropOffCutoffTime || ""}
                      onChange={(e) => setSettings((prev) => ({ ...prev, dropOffCutoffTime: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={settings.dropOffEmailEnabled}
                        onCheckedChange={(checked) => setSettings((prev) => ({ ...prev, dropOffEmailEnabled: checked }))}
                        data-testid="dropoff-email-checkbox"
                      />
                      Send by email
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={settings.dropOffSmsEnabled}
                        onCheckedChange={(checked) => setSettings((prev) => ({ ...prev, dropOffSmsEnabled: checked }))}
                        data-testid="dropoff-sms-checkbox"
                      />
                      Send by text (SMS)
                    </label>
                    {settings.dropOffSmsEnabled && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                        Each reminder sent by text counts toward your SMS usage and any applicable charges.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="dropoff-additional-note">Additional Note (optional)</Label>
                    <Input
                      id="dropoff-additional-note"
                      placeholder="Please note: If staffing ratios are full at drop-off, we will not accept late arrivals on that day."
                      value={settings.dropOffAdditionalNote || ""}
                      onChange={(e) => setSettings((prev) => ({ ...prev, dropOffAdditionalNote: e.target.value }))}
                      data-testid="dropoff-additional-note-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Added as an extra line at the end of the drop-off reminder message, which already includes
                      your cutoff time (e.g. "please be mindful of our 9:00 AM drop-off time"). Leave blank to
                      keep the message as-is.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button className="bg-amber-600 hover:bg-amber-700 text-white" disabled={saving || loading}
                onClick={handleSave} data-testid="still-here-save-btn">
                {saving ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="history">
            <div className="pt-2 space-y-3">
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" size="sm" onClick={handlePrintHistory}
                  disabled={historyLoading || history.length === 0} data-testid="still-here-history-print-btn">
                  <Printer className="w-4 h-4 mr-1.5" /> Print
                </Button>
                <Button variant="outline" size="sm"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  disabled={historyLoading || clearingHistory || history.length === 0}
                  onClick={handleClearHistory} data-testid="still-here-history-clear-btn">
                  <Trash2 className="w-4 h-4 mr-1.5" /> {clearingHistory ? "Clearing…" : "Clear History"}
                </Button>
              </div>

              {historyLoading ? (
                <p className="text-sm text-gray-500 py-8 text-center">Loading…</p>
              ) : history.length === 0 ? (
                <p className="text-sm text-gray-400 italic py-8 text-center" data-testid="still-here-history-empty">
                  No reminders sent yet.
                </p>
              ) : (
                <ScrollArea className="h-72 border border-gray-200 rounded-lg">
                  <table className="w-full text-sm" data-testid="still-here-history-table">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr className="text-left text-gray-500 text-xs">
                        <th className="px-3 py-2 font-medium">Child</th>
                        <th className="px-3 py-2 font-medium">Slot</th>
                        <th className="px-3 py-2 font-medium">Channel</th>
                        <th className="px-3 py-2 font-medium">Status</th>
                        <th className="px-3 py-2 font-medium">Sent At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h) => (
                        <tr key={h.id} className="border-t border-gray-100" data-testid={`still-here-history-row-${h.id}`}>
                          <td className="px-3 py-2 text-gray-700">{displayFirstLast(h.childName)}</td>
                          <td className="px-3 py-2 text-gray-500">
                            {h.timeSlot === "dropoff" ? "Late Drop Off" : h.timeSlot === "time1" ? "Time 1" : "Time 2"}
                          </td>
                          <td className="px-3 py-2 text-gray-500">{h.notificationType === "email" ? "Email" : "Text"}</td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className={h.success
                              ? "border-green-300 text-green-700 bg-green-50"
                              : "border-red-300 text-red-700 bg-red-50"}>
                              {h.success ? "Sent" : "Failed"}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-gray-500">{h.sentAt ? new Date(h.sentAt).toLocaleString() : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default StillHereRemindersDialog;
